import React, { useState } from "react";
import Modal from "../../../../Common/Modal";
import MtiButton from "../../../../Buttons/MtiButton";
import { ECarrier } from "../../../../../Model/ECarrier";
import CalibrationApi from "../../../../../api/CalibrationApi";
import { EModuleType } from "../../../../../Model/EModuleType";
import CheckBox from "../../../../Buttons/CheckBox";
import "./RunAutoCalibrationModal.css";
import { useTypedSelector } from "../../../../../redux/reducers/RootReducer";
import { getModuleState } from "../../../../../redux/reducers/ModuleStateReducer";
import { getModules } from "../../../../../redux/reducers/ModuleReducer";
import { CarrierState, EPsrState } from "../../../../../Model/ModuleState";
import AutoCalibrationUtil from "../../../../../Utility/AutoCalibrationUtil";
import { Ri360AutoCalibrationState } from "../../../../../Model/Calibration";
import DateUtil from "../../../../../Utility/DateUtil";

const hotelCarrierIcon = (
  <svg
    width="16"
    height="14"
    viewBox="0 0 16 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.914307 7.72483L2.1466 6.49254H10.1565V13.8863H0.914307V7.72483ZM8.92423 7.72483H7.69194V8.95713H8.92423V7.72483ZM7.07579 7.72483H5.84349V8.95713H7.07579V7.72483ZM5.22734 7.72483H3.99505V8.95713H5.22734V7.72483ZM3.3789 7.72483H2.1466V8.95713H3.3789V7.72483ZM7.69194 9.57328H8.92423V10.8056H7.69194V9.57328ZM7.69194 11.4217H8.92423V12.654H7.69194V11.4217ZM2.1466 12.654H3.3789V11.4217H2.1466V12.654ZM3.99505 12.654H5.22734V11.4217H3.99505V12.654ZM5.84349 12.654H7.07579V11.4217H5.84349V12.654ZM7.07579 9.57328V10.8056H5.84349V9.57328H7.07579ZM5.22734 9.57328V10.8056H3.99505V9.57328H5.22734ZM3.3789 9.57328V10.8056H2.1466V9.57328H3.3789Z"
      fill="#414042"
    />
    <path
      d="M5.84349 1.56335L7.07579 0.331055H15.0857V7.72483H13.2373V6.49254H13.8534V5.26024H13.2373V4.64409H13.8534V3.4118H13.2373V2.79565H13.8534V1.56335H12.6211V2.79565H12.005V1.56335H10.7727V2.79565H10.1565V1.56335H8.92423V2.79565H8.30808V1.56335H7.07579V2.79565H5.84349V1.56335Z"
      fill="#414042"
    />
    <path
      d="M3.3789 4.64409L4.6112 3.4118H12.6211V10.8056H10.7727V9.57328H11.3888V8.34098H10.7727V7.72483H11.3888V6.49254H10.7727V5.87639H11.3888V4.64409H10.1565V5.87639H9.54038V4.64409H8.30808V5.87639H7.69194V4.64409H6.45964V5.87639H5.84349V4.64409H4.6112V5.87639H3.3789V4.64409Z"
      fill="#414042"
    />
  </svg>
);

const loadPortCarrierIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.914307 4.13508V6.06755H15.0857V4.13508H0.914307Z"
      fill="#414042"
    />
    <path
      d="M3.49093 11.2208L8.00001 6.7117L12.5091 11.2208H9.28832V15.0857H6.7117V11.2208H3.49093Z"
      fill="#414042"
    />
    <path
      d="M1.55846 3.49093H14.4416L11.8649 0.914307H4.13508L1.55846 3.49093Z"
      fill="#414042"
    />
  </svg>
);

type Props = {
  show: boolean;
  onHide: () => void;
  onRunAutoCalibration: () => void;
  autoCalibrationResult: Ri360AutoCalibrationState;
  carrier: ECarrier;
};

export default function RunSingleAutoCalibrationModal({
  show,
  onHide,
  onRunAutoCalibration,
  autoCalibrationResult,
  carrier,
}: Props) {
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrModules[0].identifier).psr
  );

  if (psrState == null) {
    throw new Error("Psr state is null");
  }

  const carrierName = AutoCalibrationUtil.getCarrierName(carrier);
  const carrierIcon =
    carrier === ECarrier.LoadPort ? loadPortCarrierIcon : hotelCarrierIcon;

  const date =
    DateUtil.toDate(autoCalibrationResult.currentPositionTime) ?? new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const h = date.getHours();
  const m = date.getMinutes();

  const latestCalibrationdateStr =
    date.toLocaleDateString("en-US", options) + ` ${h}:${m < 10 ? "0" + m : m}`;

  const runAutoCalibration = () => {
    CalibrationApi.runStorageAutoCalibration("SUITE", EModuleType.PSR, [
      carrier,
    ]).then(() => {
      onRunAutoCalibration();
    });
  };

  return (
    <Modal
      show={show}
      className="auto-calibration-run-modal single"
      onHide={onHide}
      hideOnBackdropClick={true}
    >
      <Modal.Header
        title={"Auto Calibration"}
        hideButtonEnabled={false}
        className="auto-calibration-run-header single"
      />
      <Modal.Body className="auto-calibration-run-body single">
        <div className="auto-calibration-carrier-img">
          <img
            src={AutoCalibrationUtil.getCarrierImagePath(carrier)}
            alt="image"
          />
        </div>
        <div className="auto-calibration-single-info">
          <div className="question">
            Are you sure to do Auto Calibration on this carrier?
          </div>
          <div className="carrier">
            {carrierIcon}
            <span>{carrierName}</span>
          </div>
          <div className="date">
            Latest Calibration at {latestCalibrationdateStr}
          </div>
          <div className="buttons">
            <MtiButton
              variant="secondary"
              colorVariant="default"
              size="large"
              onClick={onHide}
              style={{
                width: 157,
              }}
            >
              <span>CANCEL</span>
            </MtiButton>
            <MtiButton
              disabled={psrState.state === EPsrState.Busy}
              variant="primary"
              colorVariant="default"
              size="large"
              onClick={runAutoCalibration}
              style={{
                width: 157,
              }}
            >
              <span>CALIBRATE</span>
            </MtiButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
